import type { Circuit, PlayerTrackPosition } from '../types/track.js';
import { START_LANE_LENGTH } from '../types/track.js';
import { hasStartFinishCellOverlap } from './circuitLoop.js';
import { hasStartLane } from './startLane.js';
import { normalizeMainPathIndex, cellsToJokerExit } from './jokerLap.js';
import { standingCompletedLapsOnCircuit } from './lapDisplay.js';

export interface TrackProgressOptions {
  /** Lancers cumulés — distingue la grille de départ de l’index 0 après wrap S/F. */
  totalDiceRolls?: number;
  /** Côté « départ » (index 0) quand un rival est à pathLen-1 sur la même case. */
  startSideOnSfOverlap?: boolean;
  /** 1er franchissement S/F encore à ignorer (0 si déjà consommé). */
  skipStartLineCrossingsRemaining?: number;
  /** Départ avec skip du 1er S/F (demi / finale / ligne de départ). */
  hadStartLineSkipAtStart?: boolean;
  /** Fin de manche : tous les actifs ont le même nb de lancers. */
  uniformDiceRollsAmongField?: boolean;
}

function hasPlayerConsumedStartLineSkip(options?: TrackProgressOptions): boolean {
  return (
    options?.hadStartLineSkipAtStart === true &&
    (options?.skipStartLineCrossingsRemaining ?? 1) === 0
  );
}

/** Demi / finale avec skip du 1er S/F (toute la course, par pilote). */
function isSemiFinalRaceWithStartSkip(options?: TrackProgressOptions): boolean {
  return options?.hadStartLineSkipAtStart === true;
}

/** @deprecated Alias interne — préférer les deux fonctions ci-dessus. */
function isSkipStartLineConsumed(options?: TrackProgressOptions): boolean {
  return hasPlayerConsumedStartLineSkip(options);
}

/** Base de progression (tours) alignée sur la demi / finale avant d’ajouter l’index. */
function progressLapScoreBase(
  lapCount: number,
  pathLen: number,
  rolls: number,
  semiSkipDone: boolean
): number {
  if (!semiSkipDone || rolls <= 0) return lapCount * pathLen;
  // +1 parcours pour le tour virtuel (skip S/F) : lapCount 0 → pathLen ;
  // après crédit de tours → (lapCount+2)*pathLen pour rester monotone au wrap S/F.
  if (lapCount === 0) return pathLen;
  return (lapCount + 2) * pathLen;
}

/**
 * Distance jusqu’à la sortie du joker, ramenée sur le tronçon principal entrée→sortie.
 * Sur la branche, on n’utilise pas le nombre brut de cases (le joker est plus long que
 * le raccourci) : sinon l’entrée tombe avant le split et les rivaux encore derrière
 * lancent trop tôt. Première case = juste après l’entrée ; dernière = sortie.
 */
function equivalentDistToJokerExit(
  circuit: Circuit,
  position: PlayerTrackPosition,
  rawDistToExit: number
): number {
  const jl = circuit.jokerLap;
  if (!jl || position.jokerBranchIndex == null) return rawDistToExit;
  const pathLen = circuit.path.length;
  if (pathLen <= 0) return rawDistToExit;
  const entry = normalizeMainPathIndex(jl.entryPathIndex, pathLen);
  const exit = normalizeMainPathIndex(jl.exitPathIndex, pathLen);
  const span = (exit - entry + pathLen) % pathLen;
  if (span <= 0) return rawDistToExit;
  const n = jl.cells.length;
  if (n <= 0) return rawDistToExit;
  const t = Math.min(1, (position.jokerBranchIndex + 1) / n);
  return span * (1 - t);
}

/**
 * Score de progression monotone : plus la valeur est élevée, plus le pilote est avancé.
 * Compte les cases parcourues depuis le départ (tours complets + position courante).
 *
 * Règle course (boucle S/F, ex. Hill Valley) : franchir la ligne d’arrivée puis atterrir
 * sur la case départ crédite le tour — idx 0 conserve la progression au wrap sans
 * dépasser les rivaux encore sur le même lapCount (manche ~33).
 */
export function computeTrackProgressScore(
  circuit: Circuit,
  position: PlayerTrackPosition,
  lapCount: number,
  options?: TrackProgressOptions
): number {
  const pathLen = circuit.path.length;
  if (pathLen <= 0) return 0;

  const lapBase = lapCount * pathLen;

  if (position.startLaneIndex != null && hasStartLane(circuit)) {
    // Grille : toujours derrière la case de jonction (0), sans hériter de joinPathIndex
    // (un join élevé ferait passer la ligne de départ devant le peloton).
    return lapBase - START_LANE_LENGTH - position.startLaneIndex;
  }

  const mainIdx = normalizeMainPathIndex(position.mainPathIndex, pathLen);
  const onMain = position.jokerBranchIndex == null && position.startLaneIndex == null;
  const rolls = options?.totalDiceRolls ?? 0;
  const standingOpts = {
    totalDiceRolls: rolls,
    skipStartLineCrossingsRemaining: options?.skipStartLineCrossingsRemaining,
    hadStartLineSkipAtStart: options?.hadStartLineSkipAtStart,
    startSideOnSfOverlap: options?.startSideOnSfOverlap,
    uniformDiceRollsAmongField: options?.uniformDiceRollsAmongField,
  };

  // Ouverture depuis la ligne de départ : la case de jonction n’est pas un wrap de tour.
  if (
    hasStartLane(circuit) &&
    onMain &&
    lapCount === 0 &&
    rolls > 0 &&
    !hasPlayerConsumedStartLineSkip(options)
  ) {
    const join = normalizeMainPathIndex(circuit.startLane!.joinPathIndex, pathLen);
    return (mainIdx - join + pathLen) % pathLen;
  }

  if (hasStartFinishCellOverlap(circuit) && onMain) {
    const skipConsumed = isSkipStartLineConsumed(options) && lapCount === 0 && rolls > 0;

    // 1er wrap qualif (index 0, lapCount encore 0) : crédité seulement si wrap S/F réel.
    if (mainIdx === 0 && lapCount === 0 && rolls > 0 && !skipConsumed) {
      const standing = standingCompletedLapsOnCircuit(circuit, position, lapCount, standingOpts);
      if (standing > lapCount) {
        return standing * pathLen;
      }
    }
  }

  const jl = circuit.jokerLap;
  const semiFinalSkip = isSemiFinalRaceWithStartSkip(options);

  if (jl && jl.cells.length > 0 && jl.entryPathIndex >= 0 && jl.exitPathIndex >= 0) {
    const distToExit = cellsToJokerExit(circuit, position);
    if (distToExit != null) {
      const exit = normalizeMainPathIndex(jl.exitPathIndex, pathLen);
      const skipConsumedVirtual =
        hasPlayerConsumedStartLineSkip(options) && lapCount === 0 && rolls > 0;
      // Même bande pathLen pour skip consommé / pending : ordre = position visible.
      const base = skipConsumedVirtual
        ? pathLen
        : progressLapScoreBase(lapCount, pathLen, rolls, semiFinalSkip);
      const effectiveDist = equivalentDistToJokerExit(circuit, position, distToExit);
      return base + exit - effectiveDist;
    }
  }

  if (onMain) {
    const skipVirtualLap = semiFinalSkip && lapCount === 0 && rolls > 0;

    // Tour d’ouverture (demi / finale) : classement = ordre sur la piste.
    // Skip consommé et skip pending partagent la bande pathLen+idx — un pilote
    // à idx bas (déjà passé la ligne) ne passe plus devant le peloton encore
    // à idx élevé (ex. Hansen manche ~14).
    if (skipVirtualLap) {
      if (mainIdx === pathLen - 1) {
        return pathLen + (pathLen - 1);
      }
      if (mainIdx === 0) {
        if (hasPlayerConsumedStartLineSkip(options)) {
          // Wrap S/F : même case physique que pathLen-1 — rester juste derrière
          // un rival encore en pathLen-1 (tie-break arrivalStamp).
          return pathLen + (pathLen - 1);
        }
        if (options?.startSideOnSfOverlap) {
          return pathLen - 1;
        }
        const standing = standingCompletedLapsOnCircuit(circuit, position, lapCount, standingOpts);
        return hasStartFinishCellOverlap(circuit) ? standing * pathLen : pathLen;
      }
      return pathLen + mainIdx;
    }

    const standing = standingCompletedLapsOnCircuit(circuit, position, lapCount, standingOpts);

    // Ligne d’arrivée (fin du tour en cours) — tout circuit bouclé.
    if (mainIdx === pathLen - 1) {
      // Boucle S/F (même case, ex. Hill Valley) : atterrir exactement sur
      // pathLen-1 CRÉDITE déjà le tour (voir lapCount.ts, qui compte le
      // franchissement à l'arrivée sur pathLen-1, pas au pas suivant
      // pathLen-1→0) — au repos entre deux manches, `lapCount` reflète donc
      // ce tour comme terminé et cette position est en réalité 0 case
      // parcourue sur le tour SUIVANT, pas la dernière case du tour qui
      // vient de se terminer. Sans ce garde-fou, le pilote au départ
      // (visuellement la même case que l'arrivée) surclassait à tort tout
      // rival encore en cours de tour, y compris à une case de le finir —
      // même bug, même correctif que « case départ après wrap » (idx 0)
      // ci-dessous, pour l'autre représentation de cette case physique.
      // Non appliqué au cas skip S/F (demi/finale) : sémantique différente,
      // pas couverte par cette régression.
      if (hasStartFinishCellOverlap(circuit) && !semiFinalSkip) {
        return lapBase;
      }
      if (semiFinalSkip && rolls > 0 && lapCount >= 1) {
        return progressLapScoreBase(lapCount, pathLen, rolls, true) + (pathLen - 1);
      }
      return standing * pathLen - 1;
    }

    const progressBase = progressLapScoreBase(lapCount, pathLen, rolls, semiFinalSkip);

    // Case départ après wrap.
    if (mainIdx === 0 && rolls > 0 && lapCount > 0) {
      if (semiFinalSkip && lapCount >= 3) {
        return progressBase;
      }
      if (semiFinalSkip && lapCount === 2) {
        return Math.max(progressBase, (lapCount + 1) * pathLen - 1);
      }
      if (semiFinalSkip) {
        // lapCount === 1 : comportement historique inchangé (demi/finale
        // avec skip S/F, cas non couvert par le correctif ci-dessous).
        return standing * pathLen - 1;
      }
      // Course ordinaire (pas de skip S/F) : le tour vient d'être crédité
      // (voir lapCount.ts, qui crédite dès l'arrivée sur pathLen-1 et NE
      // recrédite PAS le pas suivant pathLen-1→0) — `lapCount` reflète donc
      // déjà exactement ce tour, et `progressBase` (= lapCount*pathLen) est
      // la progression réelle (0 case parcourue sur le tour qui démarre).
      // L'ancien épinglage (`standing*pathLen-1`, qui ajoutait +1 tour
      // fictif) surclassait à tort TOUT rival encore sur le tour précédent,
      // même à une case de le terminer lui-même, ET créait une régression
      // par rapport à toute case idx>0 du même tour (ex. idx=1 avec le même
      // lapCount scorait moins que idx=0) — le vrai bug "pilote au départ
      // classé 1er à tort" (confirmé en jeu réel : idx=0/lap=1 à 57 devant
      // idx=1/lap=1 à 30, alors que ce dernier est réellement plus avancé).
      return progressBase;
    }

    // Demi / finale : juste après la case départ (lapCount 2 uniquement).
    if (semiFinalSkip && rolls > 0 && lapCount === 2 && mainIdx > 0 && mainIdx < pathLen - 1) {
      const startStanding = standingCompletedLapsOnCircuit(
        circuit,
        { mainPathIndex: 0, jokerBranchIndex: null, startLaneIndex: null },
        lapCount,
        standingOpts
      );
      const naiveMid = progressBase + mainIdx;
      const startLineScore = (lapCount + 1) * pathLen - 1;
      const cellsAfterStartCap = Math.min(15, pathLen - 2);
      if (startStanding > lapCount && mainIdx <= cellsAfterStartCap && naiveMid < startLineScore) {
        return startStanding * pathLen + mainIdx;
      }
    }
    if (semiFinalSkip && rolls > 0 && lapCount === 1 && mainIdx > 0 && mainIdx < pathLen - 1) {
      return 3 * pathLen + mainIdx;
    }
  }

  const semiFinalSkipFinal = isSemiFinalRaceWithStartSkip(options);
  const progressBaseFinal = progressLapScoreBase(lapCount, pathLen, rolls, semiFinalSkipFinal);
  return progressBaseFinal + mainIdx;
}

export function compareTrackProgress(
  circuit: Circuit,
  a: {
    position: PlayerTrackPosition;
    lapCount: number;
    totalDiceRolls?: number;
    startSideOnSfOverlap?: boolean;
    skipStartLineCrossingsRemaining?: number;
    hadStartLineSkipAtStart?: boolean;
    uniformDiceRollsAmongField?: boolean;
  },
  b: {
    position: PlayerTrackPosition;
    lapCount: number;
    totalDiceRolls?: number;
    startSideOnSfOverlap?: boolean;
    skipStartLineCrossingsRemaining?: number;
    hadStartLineSkipAtStart?: boolean;
    uniformDiceRollsAmongField?: boolean;
  }
): number {
  const progressOptions = (
    side?: boolean,
    rolls?: number,
    skip?: number,
    hadSkip?: boolean,
    uniform?: boolean
  ) => ({
    totalDiceRolls: rolls,
    startSideOnSfOverlap: side,
    skipStartLineCrossingsRemaining: skip,
    hadStartLineSkipAtStart: hadSkip,
    uniformDiceRollsAmongField: uniform,
  });
  return (
    computeTrackProgressScore(
      circuit,
      b.position,
      b.lapCount,
      progressOptions(
        b.startSideOnSfOverlap,
        b.totalDiceRolls,
        b.skipStartLineCrossingsRemaining,
        b.hadStartLineSkipAtStart,
        b.uniformDiceRollsAmongField
      )
    ) -
    computeTrackProgressScore(
      circuit,
      a.position,
      a.lapCount,
      progressOptions(
        a.startSideOnSfOverlap,
        a.totalDiceRolls,
        a.skipStartLineCrossingsRemaining,
        a.hadStartLineSkipAtStart,
        a.uniformDiceRollsAmongField
      )
    )
  );
}
